import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SugerenciasIADialog } from "@/components/SugerenciasIADialog";
import { AnchoredList } from "@/components/AnchoredList";
import { Search, X, Minus, Plus, Sparkles, PackagePlus } from "lucide-react";

// Selector de repuestos reutilizable — MISMA lógica/endpoints que el pedido de repuestos:
// búsqueda por nombre (/servicios-buscar, solo repuestos) + IA (SugerenciasIADialog).
export function RepuestoPickerDialog({ open, onOpenChange, modeloId, modeloNombre, tareaNombre, seleccion, onConfirmar }) {
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [todosModelos, setTodosModelos] = useState(false);
  const [iaOpen, setIaOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (open) { setSel((seleccion || []).map((r) => ({ ...r }))); setQ(""); setResultados([]); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const term = q.trim();
    if (!term) { setResultados([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = { q: term, limit: 25 };
        if (modeloId) { params.modelo_id = modeloId; params.todos_modelos = todosModelos; }
        const { data } = await api.get("/servicios-buscar", { params });
        setResultados(data.filter((x) => x.tipo === "repuesto"));
        setAbierto(true);
      } catch { setResultados([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, todosModelos, modeloId]);

  useEffect(() => {
    const fn = (e) => {
      if (e.target.closest && e.target.closest("[data-anchored-list]")) return;
      if (boxRef.current && !boxRef.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const agregar = (rep) => {
    setAbierto(false); setQ("");
    setSel((prev) => prev.some((r) => r.ref_id === (rep.ref_id || rep.id)) ? prev
      : [...prev, { ref_id: rep.ref_id || rep.id, codigo: rep.codigo ?? rep.codigo_parte ?? "",
          nombre: rep.nombre, categoria: rep.categoria || "", cantidad: 1 }]);
  };

  const setCant = (ref_id, delta) => setSel((prev) => prev.map((r) =>
    r.ref_id === ref_id ? { ...r, cantidad: Math.max(1, (r.cantidad || 1) + delta) } : r));
  const quitar = (ref_id) => setSel((prev) => prev.filter((r) => r.ref_id !== ref_id));

  const confirmar = () => { onConfirmar(sel); onOpenChange(false); };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="repuesto-picker-dialog" className="bg-white max-w-2xl max-h-[88vh] flex flex-col p-4 sm:p-6">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus size={18} className="text-indigo-600" /> Repuestos a cambiar{tareaNombre ? ` — ${tareaNombre}` : ""}
            </DialogTitle>
          </DialogHeader>

          <div className="shrink-0 space-y-2">
            <div className="relative" ref={boxRef}>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="repuesto-picker-buscar-input" value={q} onChange={(e) => setQ(e.target.value)}
                  onFocus={() => resultados.length && setAbierto(true)}
                  placeholder="Buscar repuesto por nombre..." className="pl-9" />
              </div>
              {abierto && resultados.length > 0 && (
                <AnchoredList anchorRef={boxRef} open={abierto && resultados.length > 0} maxHeight={280} className="rounded-lg">
                  <div data-testid="repuesto-picker-resultados">
                    {resultados.map((r) => (
                      <button key={r.ref_id} type="button" data-testid={`repuesto-picker-res-${r.ref_id}`}
                        onClick={() => agregar(r)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                        <span className="font-mono text-xs text-slate-500">{r.codigo || "—"}</span>
                        <span className="flex-1 truncate text-slate-700">{r.nombre}</span>
                        {r.en_catalogo && (
                          <span data-testid={`repuesto-picker-tag-catalogo-${r.ref_id}`}
                            className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Catálogo
                          </span>
                        )}
                        {!r.en_catalogo && r.compatible && <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-700">Compatible</span>}
                      </button>
                    ))}
                  </div>
                </AnchoredList>
              )}
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs text-slate-500">
                <input type="checkbox" data-testid="repuesto-picker-todos-modelos" checked={todosModelos}
                  onChange={(e) => setTodosModelos(e.target.checked)} className="accent-indigo-600" />
                Buscar en todos los modelos
              </label>
              <Button variant="outline" data-testid="repuesto-picker-ia-btn" onClick={() => setIaOpen(true)}
                className="h-8 border-indigo-300 text-indigo-600 hover:bg-indigo-50">
                <Sparkles size={14} className="mr-1.5" /> Preguntar a la IA
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto mt-3 space-y-2">
            {sel.length === 0 && (
              <p data-testid="repuesto-picker-vacio" className="py-8 text-center text-sm text-slate-400">
                Busque por nombre o use la IA para agregar los repuestos a cambiar.
              </p>
            )}
            {sel.map((r) => (
              <div key={r.ref_id} data-testid={`repuesto-picker-sel-${r.ref_id}`}
                className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{r.nombre}</p>
                  <p className="text-xs text-slate-400 font-mono">{r.codigo || "—"}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`repuesto-picker-menos-${r.ref_id}`} onClick={() => setCant(r.ref_id, -1)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"><Minus size={13} /></button>
                  <span data-testid={`repuesto-picker-cant-${r.ref_id}`} className="w-7 text-center text-sm font-semibold tabular-nums">{r.cantidad}</span>
                  <button data-testid={`repuesto-picker-mas-${r.ref_id}`} onClick={() => setCant(r.ref_id, 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"><Plus size={13} /></button>
                  <button data-testid={`repuesto-picker-quitar-${r.ref_id}`} onClick={() => quitar(r.ref_id)}
                    className="ml-1 w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100"><X size={14} /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="shrink-0 flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="outline" data-testid="repuesto-picker-cancelar-btn" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="repuesto-picker-confirmar-btn" onClick={confirmar}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Confirmar ({sel.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SugerenciasIADialog open={iaOpen} onOpenChange={setIaOpen} modeloId={modeloId} modeloNombre={modeloNombre || ""}
        onRepuestoListo={(rep) => agregar(rep)} />
    </>
  );
}
